import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dialog, Header3, RearangeIcon } from "@activecollab/components";
import { ReorderableList, reorderArray } from "@activecollab/yank-and-drop";
import classNames from "classnames";
import { isEqual } from "lodash";
import PropTypes from "prop-types";
import MyWorkUserProjectItem from "~/components/MyWork/MyWorkUserProjectItem";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getUserWorkProjects } from "~/store/selectors/selector.userTasks";

const DraggableProject = forwardRef(({ id, companyId, isDragging }, ref) => {
  return (
    <div
      ref={ref}
      className={classNames("tw-flex tw-items-center", { "tw-opacity-0": isDragging })}
    >
      <RearangeIcon className="tw-shrink-0 tw-mr-1" />
      <MyWorkUserProjectItem
        id={id}
        companyId={companyId}
      />
    </div>
  );
});

DraggableProject.propTypes = {
  id: PropTypes.number.isRequired,
  companyId: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
};

DraggableProject.displayName = "DraggableProject";

export const MyWorkProjectsReorderDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialProjects = useSelector(getUserWorkProjects);
  const [projects, setProjects] = useState(initialProjects);

  const onReorder = useCallback((from, to) => {
    setProjects((prevState) => reorderArray(prevState, from, to));
  }, []);

  const isReordered = useMemo(() => !isEqual(initialProjects, projects), [initialProjects, projects]);

  const submit = useCallback(async () => {
    if (isReordered) {
      await dispatch(editPersonalizedConfigOptions({ my_work_projects_order: projects.map(({ id }) => id) }));
    }
    onClose();
  }, [dispatch, isReordered, onClose, projects]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="tw-flex tw-max-w-[1000px]"
    >
      <Dialog.Title
        disableDefaultHeading
        className="tw-flex tw-justify-between tw-items-center"
      >
        <Header3>{t("Reorder Projects")}</Header3>
      </Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <ReorderableList
          onMove={onReorder}
          dragAxis="y"
        >
          {projects.map((item) => (
            <DraggableProject
              id={item.id}
              companyId={item.companyId}
              key={item.id}
            />
          ))}
        </ReorderableList>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          type="submit"
          variant="primary"
          className="tw-mr-3"
          onClick={submit}
        >
          {t("Reorder")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="tw-mr-3"
          onClick={onClose}
        >
          {t("Cancel")}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

MyWorkProjectsReorderDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

MyWorkProjectsReorderDialog.defaultProps = {
  open: false,
};
